import React, { useEffect, useRef, useState } from 'react';
import { Form, Input, Button, Card, Typography, Space, Divider, App, Modal, Select } from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined, SafetyOutlined, UnlockOutlined, HomeOutlined } from '@ant-design/icons';
import { useAuth } from '../services/auth';
import { useNavigate } from 'react-router-dom';
import passwordPolicyAPI from '../services/passwordPolicy';
import axios from 'axios';
import './Login.css';
import { useTranslation } from 'react-i18next';
import BrandLogo from '../components/BrandLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';

const { Title, Text } = Typography;

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [unlockModalVisible, setUnlockModalVisible] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [unlockMethod, setUnlockMethod] = useState('email_otp');
  const [unlockForm] = Form.useForm();
  const unlockCodeInputRef = useRef(null);
  const [mfaModalVisible, setMfaModalVisible] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaForm] = Form.useForm();
  const mfaCodeInputRef = useRef(null);
  const [resetModalVisible, setResetModalVisible] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [unlockResetToken, setUnlockResetToken] = useState('');
  const [resetForm] = Form.useForm();
  const { login, mfaChallenge, verifyMFA, requestMFALoginOTP } = useAuth();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { t } = useTranslation();

  useEffect(() => {
    if (!mfaModalVisible) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      mfaCodeInputRef.current?.focus?.();
    }, 150);

    return () => window.clearTimeout(timer);
  }, [mfaModalVisible]);

  useEffect(() => {
    if (!unlockModalVisible || (unlockMethod !== 'recovery_code' && !otpSent)) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      unlockCodeInputRef.current?.focus?.();
    }, 150);

    return () => window.clearTimeout(timer);
  }, [otpSent, unlockMethod, unlockModalVisible]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const result = await login(values.email, values.password);
      if (result?.mfaRequired) {
        // 需要二次验证
        setMfaModalVisible(true);
        message.info(t('login.mfa.required', ));
        if (result.method === 'email_otp') {
          // 自动发送一次验证码
          await requestMFALoginOTP();
          message.success(t('login.mfa.otp.sent') || '验证码已发送至邮箱');
        }
        setLoading(false);
        return;
      }
      message.success(t('login.message.success'));
      
      // 检查是否需要强制修改密码
      if (result.passwordChangeRequired) {
        try {
          const policyResponse = await passwordPolicyAPI.getPasswordPolicy();
          let warningMsg = t('login.password_policy.force_change.message');
          if (policyResponse.data && policyResponse.data.success) {
            const requirements = passwordPolicyAPI.formatPasswordRequirements(policyResponse.data.data);
            if (requirements.length > 0) {
              // 追加新密码要求列表（使用中文分隔符，后续可通过更多key细分）
              warningMsg += '。' + requirements.join('、');
            }
          }
          message.warning(warningMsg);
        } catch (error) {
          message.warning(t('login.password_policy.force_change.toast'));
        }
        navigate('/profile?tab=password&force=true');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('登录失败:', error);
      
      // 获取详细错误信息
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || t('login.message.error');
      
      // 根据HTTP状态码和错误信息提供更友好的错误提示
      if (errorMessage.includes('账户已被锁定') || errorMessage.includes('连续登录失败次数过多')) {
        message.error(t('login.locked_account.message'));
        // 自动填充解锁表单的邮箱字段
        if (values.email) {
          unlockForm.setFieldsValue({ email: values.email });
        }
        // 显示解锁账户模态框
        setUnlockModalVisible(true);
      } else if (error.response?.status === 401) {
        message.error(t('login.error.invalid_credentials'));
      } else if (error.response?.status === 403) {
        message.error(t('login.message.no_permission'));
      } else {
        message.error(errorMessage);
      }
      setLoading(false);
    }
  };

  // 处理解锁账户（两步：请求验证码 -> 验证验证码）
  const handleUnlockAccount = async () => {
    try {
      await unlockForm.validateFields(['email']);
      if (unlockMethod === 'recovery_code' || otpSent) {
        await unlockForm.validateFields(['code']);
      }

      const values = unlockForm.getFieldsValue();
      setUnlockLoading(true);

      try {
        if (unlockMethod === 'recovery_code') {
          const resp = await axios.post('/api/auth/unlock/recovery/verify', {
            email: values.email,
            code: values.code
          });
          if (resp.data?.success) {
            const token = resp.data?.data?.reset_token || '';
            if (token) {
              setUnlockResetToken(token);
              setResetModalVisible(true);
              message.success(t('login.unlock.verify.success') || '验证成功，请立即重置密码');
            } else {
              message.success(t('login.unlock.verify.success') || '验证成功，账户已解锁，请先重置密码后登录');
            }
            setUnlockModalVisible(false);
            setOtpSent(false);
            setUnlockMethod('email_otp');
            unlockForm.resetFields();
          } else {
            message.error(resp.data?.message || t('login.unlock.failed') || '操作失败');
          }
        } else if (!otpSent) {
          await axios.post('/api/auth/unlock/request', { email: values.email });
          message.success(t('login.unlock.send.success') || '如账户存在，验证码已发送至邮箱');
          setOtpSent(true);
        } else {
          const resp = await axios.post('/api/auth/unlock/verify', {
            email: values.email,
            code: values.code
          });
          if (resp.data?.success) {
            const token = resp.data?.data?.reset_token || '';
            if (token) {
              setUnlockResetToken(token);
              setResetModalVisible(true);
              message.success(t('login.unlock.verify.success') || '验证成功，请立即重置密码');
            } else {
              message.success(t('login.unlock.verify.success') || '验证成功，账户已解锁，请使用“忘记密码/修改密码”重置密码后登录');
            }
            setUnlockModalVisible(false);
            setOtpSent(false);
            unlockForm.resetFields();
          } else {
            message.error(resp.data?.message || t('login.unlock.failed') || '操作失败');
          }
        }
      } catch (error) {
        console.error('解锁账户失败:', error);
        const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || t('login.unlock.failed') || '操作失败';
        message.error(errorMessage);
      } finally {
        setUnlockLoading(false);
      }
    } catch (error) {
      // 表单验证失败
    }
  };

  const handleMFAVerify = async () => {
    try {
      const values = await mfaForm.validateFields(['code']);
      setMfaLoading(true);
      const res = await verifyMFA(values.code);
      setMfaModalVisible(false);
      message.success(t('login.message.success'));
      if (res.passwordChangeRequired) {
        try {
          const policyResponse = await passwordPolicyAPI.getPasswordPolicy();
          let warningMsg = t('login.password_policy.force_change.message');
          if (policyResponse.data && policyResponse.data.success) {
            const requirements = passwordPolicyAPI.formatPasswordRequirements(policyResponse.data.data);
            if (requirements.length > 0) {
              warningMsg += '。' + requirements.join('、');
            }
          }
          message.warning(warningMsg);
        } catch (error) {
          message.warning(t('login.password_policy.force_change.toast'));
        }
        navigate('/profile?tab=password&force=true');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || t('login.mfa.error') || '验证码错误或已过期';
      message.error(errorMessage);
    } finally {
      setMfaLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="login-content">
          <Card className="login-card" variant="borderless">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* Top-right language switcher for public page */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <LanguageSwitcher />
              </div>
              {/* Existing header content */}
              <div className="login-header">
                <div className="logo-container">
                  <BrandLogo size="lg" tone="light" />
                </div>
                <Title level={3} className="subtitle">{t('login.hero.subtitle')}</Title>
                <Text type="secondary" className="description">
                  {t('login.hero.description')}
                </Text>
              </div>
              <Divider />
              <Form
                name="login"
                onFinish={onFinish}
                autoComplete="off"
                size="large"
                layout="vertical"
              >
                <Form.Item
                  label={t('login.form.email.label')}
                  name="email"
                  rules={[
                    { required: true, message: t('login.unlock.form.email.required') },
                    { type: 'email', message: t('login.unlock.form.email.invalid') }
                  ]}
                >
                  <Input 
                    prefix={<UserOutlined />} 
                    placeholder={t('login.form.email.label')} 
                    className="login-input"
                  />
                </Form.Item>

                <Form.Item
                  label={t('login.form.password.label')}
                  name="password"
                  rules={[{ required: true, message: t('login.form.password.required', ) }]}
                >
                  <Input.Password 
                    prefix={<LockOutlined />} 
                    placeholder={t('login.form.password.label')} 
                    className="login-input"
                  />
                </Form.Item>

                <Form.Item style={{ marginTop: '32px' }}>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={loading}
                    icon={<LoginOutlined />}
                    block
                    size="large"
                    className="login-button"
                  >
                    {loading ? t('login.button.loading') : t('login.button.submit')}
                  </Button>
                </Form.Item>
              </Form>

              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <Space>
                  <Button
                    type="link"
                    icon={<HomeOutlined />}
                    onClick={() => navigate('/')}
                  >
                    {t('common.actions.back_home')}
                  </Button>
                  <Button 
                    type="link" 
                    onClick={() => setUnlockModalVisible(true)}
                    icon={<UnlockOutlined />}
                  >
                    {t('login.unlock.cta')}
                  </Button>
                </Space>
                <div className="login-contact-link-wrap">
                  <Button
                    type="link"
                    className="login-contact-link"
                    onClick={() => navigate('/contact')}
                  >
                    {t('login.contact_cta', '还没有账户？联系我们')}
                  </Button>
                </div>
              </div>
            </Space>
          </Card>
        </div>
      </div>

      {/* 解锁账户模态框 */}
      <Modal
        title={t('login.unlock.modal.title')}
        open={unlockModalVisible}
        onCancel={() => { setUnlockModalVisible(false); setOtpSent(false); setUnlockMethod('email_otp'); }}
        footer={[
          <Button key="cancel" onClick={() => setUnlockModalVisible(false)}>
            {t('common.actions.cancel')}
          </Button>,
          unlockMethod === 'email_otp' && !otpSent ? (
            <Button 
              key="send" 
              type="primary" 
              loading={unlockLoading} 
              onClick={handleUnlockAccount}
              icon={<UnlockOutlined />}
            >
              {t('login.unlock.modal.send', )}
            </Button>
          ) : (
            <Button 
              key="verify" 
              type="primary" 
              loading={unlockLoading} 
              onClick={handleUnlockAccount}
              icon={<UnlockOutlined />}
            >
              {t('login.unlock.modal.verify', )}
            </Button>
          ),
        ]}
      >
        <Form
          form={unlockForm}
          layout="vertical"
        >
          <Form.Item
            label={t('login.unlock.form.method.label', )}
          >
            <Select
              value={unlockMethod}
              onChange={(value) => {
                setUnlockMethod(value);
                setOtpSent(false);
                unlockForm.setFieldsValue({ code: undefined });
              }}
              options={[
                { value: 'email_otp', label: t('login.unlock.form.method.email', ) },
                { value: 'recovery_code', label: t('login.unlock.form.method.recovery', ) },
              ]}
            />
          </Form.Item>
          <Form.Item
            label={t('login.unlock.form.email.label')}
            name="email"
            rules={[
              { required: true, message: t('login.unlock.form.email.required') },
              { type: 'email', message: t('login.unlock.form.email.invalid') }
            ]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder={t('login.unlock.form.email.placeholder')} 
            />
          </Form.Item>
          {(unlockMethod === 'recovery_code' || otpSent) && (
            <Form.Item
              label={unlockMethod === 'recovery_code' ? t('login.unlock.form.recovery_code.label', ) : t('login.unlock.form.code.label', )}
              name="code"
              rules={[{ required: true, message: unlockMethod === 'recovery_code' ? t('login.unlock.form.recovery_code.required', ) : t('login.unlock.form.code.required', ) }]}
            >
              <Input 
                ref={unlockCodeInputRef}
                prefix={<LockOutlined />} 
                placeholder={unlockMethod === 'recovery_code' ? t('login.unlock.form.recovery_code.placeholder', ) : t('login.unlock.form.code.placeholder', )} 
                maxLength={unlockMethod === 'recovery_code' ? 32 : 6}
                onPressEnter={handleUnlockAccount}
              />
            </Form.Item>
          )}
          <div style={{ marginTop: '8px' }}>
            <Text type="secondary">
              {unlockMethod === 'recovery_code'
                ? t('login.unlock.form.recovery_code.helper', )
                : (!otpSent
                  ? t('login.unlock.form.helper', )
                  : t('login.unlock.form.helper_code', ))}
            </Text>
          </div>
        </Form>
      </Modal>

      <Modal
        title={t('login.reset.modal.title', )}
        open={resetModalVisible}
        onCancel={() => setResetModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setResetModalVisible(false)}>
            {t('common.actions.cancel')}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={resetLoading}
            onClick={async () => {
              try {
                const values = await resetForm.validateFields();
                setResetLoading(true);
                await axios.post('/api/auth/unlock/reset-password', {
                  reset_token: unlockResetToken,
                  new_password: values.newPassword,
                });
                message.success(t('login.reset.success', ));
                setResetModalVisible(false);
                setUnlockResetToken('');
                resetForm.resetFields();
              } catch (err) {
                const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || t('login.reset.failed', );
                message.error(errorMessage);
              } finally {
                setResetLoading(false);
              }
            }}
          >
            {t('login.reset.modal.submit', )}
          </Button>,
        ]}
      >
        <Form form={resetForm} layout="vertical">
          <Form.Item
            label={t('login.reset.form.new_password.label', )}
            name="newPassword"
            rules={[{ required: true, message: t('login.reset.form.new_password.required', ) }]}
          >
            <Input.Password placeholder={t('login.reset.form.new_password.placeholder', )} />
          </Form.Item>
          <Form.Item
            label={t('login.reset.form.confirm_password.label', )}
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: t('login.reset.form.confirm_password.required', ) },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('login.reset.form.confirm_password.mismatch', )));
                },
              }),
            ]}
          >
            <Input.Password placeholder={t('login.reset.form.confirm_password.placeholder', )} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 登录MFA二次验证模态框 */}
      <Modal
        title={t('login.mfa.modal.title') || '二次验证'}
        open={mfaModalVisible}
        onCancel={() => setMfaModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setMfaModalVisible(false)}>
            {t('common.actions.cancel')}
          </Button>,
          <Button 
            key="verify" 
            type="primary" 
            loading={mfaLoading} 
            onClick={handleMFAVerify}
            icon={<SafetyOutlined />}
          >
            {t('login.mfa.modal.verify') || '验证'}
          </Button>,
        ]}
      >
        <Form form={mfaForm} layout="vertical">
          <Form.Item
            label={t('login.mfa.form.code.label') || '验证码'}
            name="code"
            rules={[{ required: true, message: t('login.mfa.form.code.required') || '请输入验证码' }]}
          >
            <Input 
              ref={mfaCodeInputRef}
              prefix={<LockOutlined />} 
              placeholder={t('login.mfa.form.code.placeholder') || (mfaChallenge?.method === 'email_otp' ? '请输入邮箱验证码' : '请输入动态口令')} 
              maxLength={6}
              onPressEnter={handleMFAVerify}
            />
          </Form.Item>
          {mfaChallenge?.method === 'email_otp' && (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="small" onClick={async () => {
                await requestMFALoginOTP();
                message.success(t('login.mfa.otp.resent') || '验证码已重新发送');
              }}>{t('login.mfa.actions.resend') || '重新发送'}</Button>
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">
              {mfaChallenge?.method === 'email_otp'
                ? (t('login.mfa.helper.email') || '我们已向您的邮箱发送验证码')
                : (t('login.mfa.helper.totp') || '请在身份验证器中查看并输入当前 6 位动态口令（一次性验证码，不是登录密码）')}
            </Text>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Login;
